import { Help } from '@mui/icons-material'
import { Checkbox, Link, Tooltip, Typography } from '@mui/material'
import { NEON_BLUE_LIGHT } from '@returnmates/client-core/src/constants/colors'
import {
  methodMap,
  NEGATIVE_DELIVERY_CSAT_REASON_TYPE_MAP,
  NEGATIVE_PICKUP_CSAT_REASON_TYPE_MAP,
  POSITIVE_CSAT_REASON_TYPE_MAP,
} from '@returnmates/client-core/src/constants/trip'
import {
  Trip,
  TripCSATReasonType,
  TripStatus,
  TripType,
  TripVariant,
} from '@returnmates/client-core/src/graphql/generated/api'
import Chip from '@returnmates/ui-core/src/components/Chip'
import Input from '@returnmates/ui-core/src/components/Input'
import clsx from 'clsx'
import moment from 'moment'
import { memo, useCallback, useEffect, useState } from 'react'
import { Field, useField } from 'react-final-form'

import CalendarIcon from '../../../components/images/icons/calendar'
import Select from '../../../components/Select'
import SingleDatePicker from '../../../components/SingleDatePicker'
import { CSATScoreMap, statusesForCompletionNotes } from '../constants'
import useStyles from '../styles'

interface Props {
  currentTrip: Trip | null
}

type TagType = {
  id: string
  label: string
  isSelected: boolean
}

function Information({ currentTrip }: Props) {
  const classes = useStyles()

  const { input: statusInput } = useField<TripStatus>('status')
  const { input: tripCSATScoreInput } = useField('tripCSATScore')
  const { input: tripCSATReasonInput } = useField('tripCSATReason')
  const [tags, setTags] = useState<TagType[] | null>(null)

  const handleReasonSelect = useCallback(
    reason => {
      if (tags) {
        const updatedTags = tags.map(tag => {
          if (tag.id === reason) {
            return {
              ...tag,
              isSelected: !tag.isSelected,
            }
          }

          return tag
        })
        setTags(updatedTags)

        const selectedReasons = updatedTags?.reduce((acc: string[], tag) => {
          if (tag.isSelected) {
            acc.push(tag.id)
          }
          return acc
        }, [])

        if (selectedReasons?.length) {
          tripCSATReasonInput.onChange(selectedReasons)
        } else {
          tripCSATReasonInput.onChange(null)
        }
      }
    },
    [tags, tripCSATReasonInput],
  )

  useEffect(() => {
    //TODO: refactor this logic in the future here, and also in the CSATModal component
    let tagsObj
    if (currentTrip?.type === TripType.PICKUP) {
      if (Number(tripCSATScoreInput.value) < 4) {
        tagsObj = Object.keys(NEGATIVE_PICKUP_CSAT_REASON_TYPE_MAP).map(
          (key: TripCSATReasonType) => ({
            id: key,
            label: NEGATIVE_PICKUP_CSAT_REASON_TYPE_MAP[key],
            isSelected: currentTrip.tripCSATReason?.includes(key),
          }),
        ) as TagType[]

        setTags(tagsObj as TagType[])
      } else if (Number(tripCSATScoreInput.value) >= 4) {
        tagsObj = Object.keys(POSITIVE_CSAT_REASON_TYPE_MAP).map((key: TripCSATReasonType) => {
          const value = POSITIVE_CSAT_REASON_TYPE_MAP[key]

          if (typeof value === 'function') {
            return {
              id: key,
              label: value('Pickup', 'picked up'),
              isSelected: currentTrip.tripCSATReason?.includes(key),
            }
          } else {
            return {
              id: key,
              label: value,
              isSelected: currentTrip.tripCSATReason?.includes(key),
            }
          }
        })

        setTags(tagsObj as TagType[])
      }
    } else {
      if (Number(tripCSATScoreInput.value) < 4) {
        tagsObj = Object.keys(NEGATIVE_DELIVERY_CSAT_REASON_TYPE_MAP).map(
          (key: TripCSATReasonType) => ({
            id: key,
            label: NEGATIVE_DELIVERY_CSAT_REASON_TYPE_MAP[key],
            isSelected: currentTrip?.tripCSATReason?.includes(key),
          }),
        ) as TagType[]

        setTags(tagsObj as TagType[])
      } else if (Number(tripCSATScoreInput.value) >= 4) {
        tagsObj = Object.keys(POSITIVE_CSAT_REASON_TYPE_MAP).map((key: TripCSATReasonType) => {
          const value = POSITIVE_CSAT_REASON_TYPE_MAP[key]

          if (typeof value === 'function') {
            return {
              id: key,
              label: value('Delivery', 'delivered'),
              isSelected: currentTrip?.tripCSATReason?.includes(key),
            }
          } else {
            return {
              id: key,
              label: value,
              isSelected: currentTrip?.tripCSATReason?.includes(key),
            }
          }
        })

        setTags(tagsObj as TagType[])
      }
    }
  }, [currentTrip?.tripCSATReason, currentTrip?.type, tripCSATScoreInput.value])

  return (
    <>
      {currentTrip?.type === TripType.PICKUP ? (
        <div className={classes.detailsRow}>
          <div className={clsx(classes.inputHead, classes.detailsTypeWithHeadTooltip)}>
            <p>Trip Variant</p>

            <Tooltip
              className={classes.headTooltip}
              title={
                <span>
                  <b>Item-based pickups</b> allow users and drivers to update the status of specific
                  pickups. <br /> <b>Order-based pickups</b> set the status of all packages in the
                  trip based on the trip’s status. <br /> <b>Seller pickups</b> require customers to
                  print and attach a label to the packages for pickup.
                </span>
              }
              arrow
            >
              <Help className={classes.helpIcon} />
            </Tooltip>
          </div>

          <div className={classes.inputText}>
            {currentTrip?.variant || TripVariant.ITEM_BASED_PICKUP}
          </div>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Wrong Item Picked Up</p>
        <Field name="isTripWithWrongPickedUpItem">
          {({ input }) => (
            <Checkbox
              checked={input.value}
              className={classes.wrongPickedUpItemCheckbox}
              onChange={input.onChange}
            />
          )}
        </Field>
      </div>
      {currentTrip?.expectedDate ? (
        <div className={classes.detailsRow}>
          <div className={classes.inputHead}>Expected Date</div>
          <Typography>{moment(currentTrip?.expectedDate).format('MM-DD-YYYY')}</Typography>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Date</p>
        <Field name="date">
          {({ input, meta }) => (
            <SingleDatePicker
              placeholder="Select date"
              momentFormat="YYYY-MM-DD"
              fullWidth
              value={input.value}
              onChange={input.onChange}
              keyboardIcon={CalendarIcon}
              className={classes.datePicker}
              disabled
              error={meta.touched && meta.error}
              helperText={meta.error}
              disablePast
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Method</p>
        <Field name="method">
          {({ input }) => (
            <Select
              fullWidth
              value={input.value}
              onChange={input.onChange}
              values={methodMap}
              disabled
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>CSAT Score</p>
        <Field name="tripCSATScore">
          {({ input }) => (
            <Select
              fullWidth
              value={Number(input.value)}
              onChange={input.onChange}
              values={CSATScoreMap}
              placeholder=""
            />
          )}
        </Field>
      </div>
      {tripCSATScoreInput.value && tripCSATScoreInput.value > 0 ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>CSAT Reason</p>
          <div>
            {tags?.map(tag => (
              <Chip
                key={tag.id}
                tag={tag}
                onSelect={handleReasonSelect}
                activeColor={NEON_BLUE_LIGHT}
              />
            ))}
          </div>
        </div>
      ) : null}
      {tripCSATScoreInput.value && tripCSATScoreInput.value !== '0' ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>CSAT Details</p>
          <Field name="tripCSATText">
            {({ input, meta }) => (
              <Input
                placeholder="Add CSAT Feedback"
                classes={{
                  textField: classes.input,
                }}
                multiline
                onChange={input.onChange}
                value={input.value}
                fullWidth
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Notes</p>
        <Field name="notes">
          {({ input, meta }) => (
            <Input
              placeholder="Add notes"
              classes={{
                textField: classes.input,
              }}
              multiline
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>
      {statusesForCompletionNotes.includes(statusInput.value) ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Completion Notes</p>
          <Field name="completionNotes">
            {({ input, meta }) => (
              <Input
                placeholder="Add Completion Notes"
                classes={{
                  textField: classes.input,
                }}
                multiline
                onChange={input.onChange}
                value={input.value}
                fullWidth
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
      ) : null}

      {currentTrip?.trackingUrl ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Onfleet Tracking</p>
          <Link href={currentTrip.trackingUrl} className={classes.link} target="_blank">
            VIEW TRACKING
          </Link>
        </div>
      ) : currentTrip?.taskId ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Onfleet Task Id</p>
          <Typography>{currentTrip?.taskId}</Typography>
        </div>
      ) : null}
    </>
  )
}

export default memo(Information)
